/**
 * doctrinalCertaintyScale.js
 * Maps the current request context to a certainty level (1–5),
 * which shapes how Kevia attributes and qualifies its responses.
 *
 * Level 1 — Contextual suggestion (no doctrinal basis)
 * Level 2 — Pastoral principle (inferred from tradition)
 * Level 3 — Denominational guidance (EGW or Church Manual cited)
 * Level 4 — Clear biblical principle (thematic, no explicit reference)
 * Level 5 — Explicit biblical text (direct reference retrieved or cited)
 */

const LEVEL_META = {
  1: {
    label:               "sugerencia contextual",
    shouldAttributeSource: false,
    promptGuidance:      "Habla desde experiencia pastoral, no desde autoridad doctrinal. Usa frases como 'desde mi perspectiva' o 'en situaciones así'.",
  },
  2: {
    label:               "principio pastoral",
    shouldAttributeSource: false,
    promptGuidance:      "Puedes referenciar principios generales de cuidado pastoral, pero sin atribuirlos a fuentes específicas.",
  },
  3: {
    label:               "guía denominacional",
    shouldAttributeSource: true,
    promptGuidance:      "Puedes citar con precisión Elena G. White o el Manual de Iglesia si se recuperó contexto. Indica la fuente clara y brevemente.",
  },
  4: {
    label:               "principio bíblico",
    shouldAttributeSource: true,
    promptGuidance:      "Fundamenta la respuesta en principios bíblicos sin citar un texto específico. Usa lenguaje como 'la Escritura enseña que...'.",
  },
  5: {
    label:               "texto bíblico explícito",
    shouldAttributeSource: true,
    promptGuidance:      "Cita el texto bíblico directamente. Incluye referencia (libro, capítulo, versículo) y versión si está disponible.",
  },
};

/**
 * @param {{
 *   intent:             string,
 *   bibleQuery:         object | null,
 *   denominationalPlan: object | null,
 *   ethics:             object,
 * }} params
 * @returns {{
 *   level:               number,
 *   label:               string,
 *   shouldAttributeSource: boolean,
 *   promptGuidance:      string,
 * }}
 */
export function getDoctrinalCertaintyLevel({ intent, bibleQuery, denominationalPlan, ethics }) {
  // Level 5 — explicit Bible text retrieved or directly referenced
  if (bibleQuery?.detected && bibleQuery?.reference) {
    return _build(5);
  }

  // Level 4 — Bible-adjacent intent without specific reference (semantic retrieval)
  if (bibleQuery?.detected && !bibleQuery?.reference) {
    return _build(4);
  }
  if (intent === "sermon" || intent === "reflection" || intent === "bible") {
    return _build(4);
  }

  // Level 3 — denominational sources explicitly planned
  if (denominationalPlan?.sourceTypes?.length > 0) {
    const hasDenom = denominationalPlan.sourceTypes.some((s) =>
      ["egw", "church_manual"].includes(s)
    );
    if (hasDenom) return _build(3);
  }

  // Level 2 — pastoral counsel without denominational sources
  if (intent === "pastoral" || intent === "prayer" || ethics?.requiresSoftTone) {
    return _build(2);
  }

  // Level 1 — doctrinal caution required (discipline, apostasy) or general intent
  if (ethics?.requiresDoctrinalCaution || intent === "general" || !intent) {
    return _build(1);
  }

  return _build(2);
}

function _build(level) {
  return { level, ...LEVEL_META[level] };
}
